import { memo } from 'react';
import type { FC } from 'react';
import cn from 'classnames';
import { InputNumber, Select, Tooltip } from 'antd';
import {
  FileTextOutlined,
  RetweetOutlined,
  FieldTimeOutlined,
  QuestionCircleOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
} from '@ant-design/icons';
import { ScheduleDay } from '../../../../hooks/components/useClinicSettingsDetail';
import { ScheduleTable } from '../ScheduleTable/ScheduleTable';
import styles from '../styles.module.css';
import { ScenarioParticipation } from './ScenarioParticipation';
import type { ScenarioParticipationProps } from './ScenarioParticipation';

const CALLS_MAX_TRIES_TOOLTIP =
  'Максимум звонков одному пациенту (включая первый)';
const CALLS_INTERVAL_TOOLTIP =
  'Минимальная пауза между звонками одному пациенту; может быть больше из‑за очереди обзвона';

const SCENARIO_OPTIONS = [
  { value: 'slot', label: 'Напоминание о приёме' },
  { value: 'notification', label: 'Информирование' },
  { value: 'dispensarization', label: 'Диспансеризация' },
  { value: 'form', label: 'Анкетирование' },
];

const SCENARIO_ACTIVITY_OPTIONS = [
  { value: 'active', label: 'Активен' },
  { value: 'inactive', label: 'Не активен' },
];

interface Props extends ScenarioParticipationProps {
  clinicName: string;
  scenarioType: string;
  onScenarioTypeChange: (v: string) => void;
  callsMaxTries: number;
  onCallsMaxTriesChange: (v: number) => void;
  callsInterval: number;
  onCallsIntervalChange: (v: number) => void;
  onCallsDisableChange: (v: boolean) => void;
  /** Часовой пояс пользователя (напр. «UTC+3») */
  utcLabel: string;
  /** График обзвона */
  callSchedule: ScheduleDay[];
  callHolidays: string[];
  onCallScheduleChange: (
    day: string,
    field: 'start' | 'end',
    value: string | null,
  ) => void;
  onCallToggleDay: (day: string) => void;
  onCallAddHoliday: (date: string) => void;
  onCallRemoveHoliday: (date: string) => void;
  /** Загрузка графика обзвона */
  callScheduleLoading?: boolean;
}

export const ScenarioSettings: FC<Props> = memo(
  ({
    scenarioType,
    onScenarioTypeChange,
    callsMaxTries,
    onCallsMaxTriesChange,
    callsInterval,
    onCallsIntervalChange,
    callsDisable,
    onCallsDisableChange,
    excludedDoctorIds,
    onToggleSpecialization,
    onToggleDoctor,
    doctorSearch,
    onDoctorSearchChange,
    specFilter,
    onSpecFilterChange,
    specializations,
    doctors,
    utcLabel,
    callSchedule,
    callHolidays,
    onCallScheduleChange,
    onCallToggleDay,
    onCallAddHoliday,
    onCallRemoveHoliday,
    callScheduleLoading,
    disabled = false,
  }) => {
    const isCallsDisabled = callsDisable === true;
    const activityValue = isCallsDisabled ? 'inactive' : 'active';

    const handleActivityChange = (value: string) => {
      onCallsDisableChange(value === 'inactive');
    };

    return (
      <div className={styles.scenarioBlock}>
        {/* Тип сценария / Активность / Попытки / Интервал — в одну строку */}
        <div className={cn(styles.fieldRow, styles.scenarioPadded)}>
          <div className={styles.field}>
            <label className={styles.fieldLabel}>
              <FileTextOutlined className={styles.fieldIcon} />
              Тип сценария
            </label>
            <Select
              className={styles.fieldInput}
              value={scenarioType}
              onChange={onScenarioTypeChange}
              options={SCENARIO_OPTIONS}
              disabled={disabled}
            />
          </div>
          <div className={styles.field}>
            <label className={styles.fieldLabel}>
              <CheckCircleOutlined className={styles.fieldIcon} />
              Активность сценария
            </label>
            <Select
              className={styles.fieldInput}
              value={activityValue}
              onChange={handleActivityChange}
              options={SCENARIO_ACTIVITY_OPTIONS}
              disabled={disabled}
            />
          </div>
          <div className={styles.field}>
            <label className={styles.fieldLabel}>
              <RetweetOutlined className={styles.fieldIcon} />
              Количество попыток
              <Tooltip title={CALLS_MAX_TRIES_TOOLTIP}>
                <QuestionCircleOutlined className={styles.fieldHelpIcon} />
              </Tooltip>
            </label>
            <InputNumber
              className={styles.fieldInput}
              value={callsMaxTries}
              onChange={(v) => onCallsMaxTriesChange(v ?? 1)}
              min={1}
              max={10}
              disabled={disabled}
            />
          </div>
          <div className={styles.field}>
            <label className={styles.fieldLabel}>
              <FieldTimeOutlined className={styles.fieldIcon} />
              Интервал в минутах
              <Tooltip title={CALLS_INTERVAL_TOOLTIP}>
                <QuestionCircleOutlined className={styles.fieldHelpIcon} />
              </Tooltip>
            </label>
            <InputNumber
              className={styles.fieldInput}
              value={callsInterval}
              onChange={(v) => onCallsIntervalChange(v ?? 60)}
              min={1}
              disabled={disabled}
            />
          </div>
        </div>

        {/* Блок «Параметры сценария» */}
        {scenarioType === 'slot' && (
          <ScenarioParticipation
            callsDisable={callsDisable}
            excludedDoctorIds={excludedDoctorIds}
            onToggleSpecialization={onToggleSpecialization}
            onToggleDoctor={onToggleDoctor}
            doctorSearch={doctorSearch}
            onDoctorSearchChange={onDoctorSearchChange}
            specFilter={specFilter}
            onSpecFilterChange={onSpecFilterChange}
            specializations={specializations}
            doctors={doctors}
            disabled={disabled}
          />
        )}

        {/* Блок «График обзвона» — виден для всех типов сценария */}
        <div className={styles.scenarioPadded}>
          <label className={styles.fieldHeader}>
            <ClockCircleOutlined className={styles.fieldIcon} />
            {`График обзвона (${utcLabel})`}
          </label>
          <span className={styles.scheduleSubtitle}>
            Время, в которое запускаются задания исходящего обзвона по
            выбранному сценарию
          </span>
        </div>

        <ScheduleTable
          schedule={callSchedule}
          holidays={callHolidays}
          onScheduleChange={onCallScheduleChange}
          onToggleDay={onCallToggleDay}
          onAddHoliday={onCallAddHoliday}
          onRemoveHoliday={onCallRemoveHoliday}
          timezone={utcLabel}
          loading={callScheduleLoading}
          startColumnTitle="Начало обзвона"
          endColumnTitle="Окончание обзвона"
          modalHint="Выбранные даты будут исключены из графика исходящего обзвона"
          endColumnHelpTooltip="Время окончания интервала, в который система запускает задачи обзвона. Укажите на 1 минуту раньше требуемого срока. Пример: 17:59 вместо 18:00"
          disabled={disabled}
        />
      </div>
    );
  },
);

ScenarioSettings.displayName = 'ScenarioSettings';
